
import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { strings } from './strings';

const PageSection = styled.section`
  padding: 4rem 0;
  direction: rtl;
`;

const PageContainer = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const CardText = styled.p`
  color: #374151;
  line-height: 1.625;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  color: #374151;
  max-width: 48rem;
  margin: 0 auto;
`;

const ProcessGrid = styled.div`
  display: grid;
  gap: 2rem;
`;

const ProcessStep = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  opacity: 0;
  transform: translateY(50px);
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  ${props => props.visible && `
    opacity: 1;
    transform: translateY(0);
  `}
`;

const StepNumber = styled.div`
  flex-shrink: 0;
  width: 4rem;
  height: 4rem;
  background-color: #ea580c;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.125rem;
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 0.2s;
  
  ${props => props.visible && `
    opacity: 1;
    transform: scale(1);
  `}
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 0.75rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 0.4s;
  position: relative;
  
  ${props => props.visible && `
    opacity: 1;
    transform: translateY(0);
  `}
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    right: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #ea580c, #f97316);
    border-radius: 1px;
    transition: width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transition-delay: 0.8s;
  }
  
  ${props => props.visible && `
    &::after {
      width: 3rem;
    }
  `}
`;

const StepDescription = styled.p`
  color: #374151;
  line-height: 1.625;
  opacity: 0;
  transform: translateY(15px);
  transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 0.6s;
  
  ${props => props.visible && `
    opacity: 1;
    transform: translateY(0);
  `}
  
  strong {
    color: #ea580c;
    font-weight: 700;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, #ea580c, #f97316);
      border-radius: 1px;
      opacity: 0.3;
    }
  }
`;
const InfoCard = styled.div`
  background-color: #fff7ed;
  padding: 2rem;
  border-radius: 0.5rem;
  margin-top: 4rem;
`;

const ContactSection = styled.section`
  background-color: #ea580c;
  padding: 4rem 0;
  direction: rtl;
`;

const ContactContainer = styled.div`
  max-width: 42rem;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const ContactHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const ContactTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: bold;
  color: white;
  margin-bottom: 1rem;
`;

const ContactSubtitle = styled.p`
  color: #fed7aa;
`;

const ContactForm = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

// Add loading spinner for homepage
const HomeLoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-left: 0.5rem;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Update existing Input and SubmitButton for disabled states
const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ea580c;
    box-shadow: 0 0 0 2px rgba(234, 88, 12, 0.2);
  }
  
  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #ea580c;
  color: white;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #c2410c;
  }
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

const ProcessPage = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [visibleSteps, setVisibleSteps] = useState({});
  const stepRefs = useRef([]);

  // Intersection Observer for scroll animations
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const stepIndex = stepRefs.current.indexOf(entry.target);
            if (stepIndex !== -1) {
              setVisibleSteps(prev => ({ ...prev, [stepIndex]: true }));
            }
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    stepRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async () => {
    if (!name || !email) {
      alert(strings.home.contact.fillAllFields);
      return;
    }

    setIsLoading(true);

    try {
      const templateParams = {
        from_name: name,
        from_email: email,
        from_phone: phone || 'לא צוין', // Use actual phone or default
        message: `בקשה להתחלת מסע כושר מדף הבית. אנא צרו קשר לתיאום פגישת ייעוץ ראשונית.`,
        to_email: 'haggaimagen87@gmail.com'
      };

      await emailjs.send(
        'service_w3pkjf6',
        'template_lwf20nq',
        templateParams,
        'Sgy1Do0paUmA8QyPR'
      );

      alert(`${strings.home.contact.thankYouMessage} ${name}! ההודעה נשלחה בהצלחה. ${strings.home.contact.followUpMessage} ${email} ${strings.home.contact.soon}.`);
      setEmail('');
      setName('');
      setPhone(''); // Reset phone field
    } catch (error) {
      console.error('EmailJS Error:', error);
      alert('שגיאה בשליחת ההודעה. אנא נסה שוב או צור קשר טלפונית.');
    } finally {
      setIsLoading(false);
    }
  };

  const steps = [
    {
      number: "01",
      title: "אבחון מעמיק ללא שיפוטיות",
      description: "לא נתחיל עם שום תוכנית גנרית. במקום זה, נבין יחד מה באמת קורה עכשיו בחיים שלך - איפה אתה תקוע, מה כבר ניסית בעבר ולמה זה לא עבד. נזהה את הדפוסים שחוזרים על עצמם ונבין מה המכשולים האמיתיים שמעכבים אותך."
    },
    {
      number: "02", 
      title: "בניית מסלול השינוי האישי שלך",
      description: "נבנה תוכנית שמתחשבת באורח החיים שלך, בלוח הזמנים שלך ובמגבלות שלך. לא נשאיר אותך עם רשימה של דברים לעשות - נמצא יחד איך להכניס שינויים קטנים לשגרת היום שלך באופן טבעי. שינויים שמצטברים לתוצאות עצומות, <strong>מבלי להרגיש שאתה נלחם עם עצמך כל יום<strong>."
    },
    {
      number: "03",
      title: "כלים מעשיים למוטיבציה עצמית",
      description: "המוטיבציה שמגיעה מבחוץ נגמרת מהר. נלמד כלים פשוטים אבל שיעזרו לך לייצר מוטיבציה פנימית שלא תלויה באף גורם חיצוני. נבין איך להתמודד עם רגעים קשים, איך לחזור למסלול אחרי מעידה, ואיך לשמור על המומנטום גם כשהחיים מסתבכים."
    },
    {
      number: "04",
      title: "פירוק מטרות למדרגות הצלחה",
      description: "המטרה 'לרדת 15 קילו' או 'להיות בכושר' מרגישה מפחידה ורחוקה. נלמד איך לקחת את המטרה הגדולה ולפרק אותה לצעדים קטנים שאפשר לבצע כבר היום. כל הצלחה קטנה תחזק את הביטחון שלך ותדליק את המוטיבציה להמשיך."
    },
    {
      number: "05",
      title: "מעקב חכם שמעודד התקדמות",
      description: "לא נסתפק בלעקוב אחרי המספר על המשקל. נבין יחד איך לזהות התקדמות גם במקומות שלא חשבת עליהם - רמת האנרגיה, איכות השינה, הביטחון העצמי, כושר הסבולת. נבצע התאמות חכמות בתוכנית כדי לוודא שהיא ממשיכה לעבוד בשבילך. התקדמות אמיתית זה לא קו ישר - נלמד איך לזהות את הכיוון הנכון גם כשיש עליות וירידות."
    },
    {
      number: "06",
      title: "בניית סביבה שמעודדת הצלחה",
      description: "השינוי האמיתי קורה כשהסביבה שלך עובדת איתך ולא נגדך. נבין יחד איך לארגן את הבית, את לוח הזמנים ואת הסביבה החברתית שלך כך שיהיה לך קל להצליח וקשה להיכשל. נלמד איך להתמודד עם לחצים חברתיים, איך לתקשר עם המשפחה שלך על השינוי, ואיך ליצור מערכת תמיכה שתחזיק אותך גם ברגעים הקשים."
    },
    {
      number: "07",
      title: "זיהוי ומניעת דפוסי כישלון",
      description: "יש דברים מסוימים שאתה עושה ששמים אותך במסלול ישר לכישלון, גם אם אתה לא מודע להם. נפתח מודעות לדפוסים האלה - הקשר בין רגשות ואכילה, ההתנהלות בסטרס, ההרגלים שגורמים לך לוותר. במקום להיאבק עם התוצאות, נלמד למנוע את הבעיות מהמקור."
    },
    {
      number: "08",
      title: "מעבר לעצמאות ושמירה על התוצאות",
      description: "המטרה היא שתוכל להמשיך בלעדיי. נבנה תוכנית ארוכת טווח שתשמור עליך במסלול, תמנע ממך לחזור לאחור ותעזור לך להמשיך לצמוח. נוודא שאתה מצויד בכלים, בביטחון ובמערכת התמיכה הנדרשים כדי להתמודד עם אתגרים עתידיים. זה לא רק על להגיע למטרה - זה על לשמור עליה לכל החיים."
    }
  ];

  return (
    <PageSection>
      <PageContainer>
        <PageHeader>
          <PageTitle>התהליך שנעבור יחד</PageTitle>
          <PageSubtitle>
            רצף של שמונה מפגשים שבועיים בליווי צמוד ומותאם אישית עבורך ועבור היעדים שלך.
            <br />
            בעוד שלושה חודשים מהיום תיראה יותר טוב, תרגיש יותר טוב - ותהיה חיוני ומלא באנרגיה
            <br />
            <strong>מבלי להרגיש שאתה נלחם עם עצמך כל יום</strong>.
          </PageSubtitle>
        </PageHeader>

        <ProcessGrid>
          {steps.map((step, index) => (
            <ProcessStep 
              key={index}
              visible={visibleSteps[index]}
              ref={el => stepRefs.current[index] = el}
            >
              <StepNumber visible={visibleSteps[index]}>{step.number}</StepNumber>
              <StepContent>
                <StepTitle visible={visibleSteps[index]}>{step.title}</StepTitle>
                <StepDescription 
                  visible={visibleSteps[index]}
                  dangerouslySetInnerHTML={{ __html: step.description }} 
                />
              </StepContent>
            </ProcessStep>
          ))}
        </ProcessGrid>

        <InfoCard>
          <CardTitle>למה התהליך שלנו עובד</CardTitle>
          <CardText>
            הגישה המובנית של התהליך מבטיחה שכל היבט של השינוי שלך מתוכנן ומבוצע בקפידה. 
            על ידי פירוק המסע לשלבים ניתנים לניהול, מתבטלת התחושה של בילבול ונוצרות נקודות ציון ברורות 
            ששומרות עליך במוטיבציה גבוהה ובמסלול להצלחה. המתודולוגיה השיטתית הזו הוכיחה עצמה כמוצלחת לעשרות 
            לקוחות, ללא קשר לנקודת המוצא או הרקע שלהם.
          </CardText>
        </InfoCard>

        <ContactSection>
          <ContactContainer>
            <ContactHeader>
              <ContactTitle>{strings.home.contact.title}</ContactTitle>
              <ContactSubtitle>{strings.home.contact.subtitle}</ContactSubtitle>
            </ContactHeader>
            <ContactForm>
              <FormGrid>
                <FormGroup>
                  <Label htmlFor="name">{strings.home.contact.nameLabel}</Label>
                  <Input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={strings.home.contact.namePlaceholder}
                    disabled={isLoading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="email">{strings.home.contact.emailLabel}</Label>
                  <Input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={strings.home.contact.emailPlaceholder}
                    disabled={isLoading}
                  />
                </FormGroup>
                {/* New Phone Field */}
                <FormGroup>
                  <Label htmlFor="phone">מספר טלפון</Label>
                  <Input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="050-"
                    disabled={isLoading}
                  />
                </FormGroup>
                <SubmitButton onClick={handleSubmit} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      שולח...
                      <HomeLoadingSpinner />
                    </>
                  ) : (
                    strings.home.contact.submitButton
                  )}
                </SubmitButton>
              </FormGrid>
            </ContactForm>
          </ContactContainer>
        </ContactSection>
      </PageContainer>
    </PageSection>
  );
};

export default ProcessPage;